/**
 * The street map's pinned city header (MT-01): the name of the city in view, in screen space
 * under the top bar, cross-fading when the journey scrolls into another city. A lock marks a
 * city the player cannot visit yet. Cards are rebuilt only when the city changes, never per frame.
 * A canvas text keeps the face it was drawn with, so a name that needs the Vietnamese fallback
 * (a Hanoi pack's, a hint's) waits for that face (game/glyphs.ts) before its card is drawn; the
 * map loads the names it knows first, this covers any that comes later (a pack registered while
 * the map is up).
 */
import type * as Phaser from 'phaser';
import type { Art } from '../game/art.js';
import { C, FONT, css } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { deco } from '../fx/motion.js';
import { glyphsReady, loadGlyphs } from '../game/glyphs.js';

export class CityHeader {
  private card: Phaser.GameObjects.Container | null = null;
  /** the name on the card, or on the card waiting for its face (test hooks) */
  text = '';
  private locked = false;
  /** the card for `text` waits for the Vietnamese face before it is drawn */
  private waiting = false;

  /** `x`: centre; `y`: top edge; `maxW`: widest the card may be (it must clear the feature rail). */
  constructor(
    private scene: Phaser.Scene,
    private art: Art,
    private x: number,
    private y: number,
    private maxW: number,
  ) {
    // Phaser reuses the scene object: a card still waiting for its face when the map shuts down
    // (a resize, a level started) must not be drawn into the map's next run, which has its own header
    scene.events.once('shutdown', () => (this.waiting = false));
  }

  show(name: string, locked: boolean): void {
    if (name === this.text && locked === this.locked && (this.card || this.waiting)) return;
    this.text = name;
    this.locked = locked;
    if (!glyphsReady([name])) {
      // drawn once the face has arrived, unless the header has moved on to another city (or the map shut down) meanwhile
      this.waiting = true;
      void loadGlyphs([name]).then(() => {
        if (this.text !== name || this.locked !== locked || !this.waiting) return;
        this.waiting = false;
        if (this.scene.sys.isActive()) this.swap(name, locked);
      });
      return;
    }
    this.waiting = false;
    this.swap(name, locked);
  }

  private swap(name: string, locked: boolean): void {
    const old = this.card;
    const card = this.build(name, locked);
    this.card = card;
    const d = deco(240);
    if (!old || !d) {
      old?.destroy();
      return;
    }
    const u = vp.u;
    this.scene.tweens.killTweensOf(old);
    this.scene.tweens.add({ targets: old, alpha: 0, y: this.y - 6 * u, duration: d, ease: 'Sine.easeIn', onComplete: () => old.destroy() });
    card.setAlpha(0).setY(this.y + 6 * u);
    this.scene.tweens.add({ targets: card, alpha: 1, y: this.y, duration: d, ease: 'Sine.easeOut' });
  }

  private build(name: string, locked: boolean): Phaser.GameObjects.Container {
    const u = vp.u;
    const s = this.scene;
    const h = 36 * u;
    const pad = 16 * u;
    const icon = locked ? 18 * u : 0;
    const gap = locked ? 6 * u : 0;
    const label = s.add.text(0, h / 2, name, { fontFamily: FONT, fontSize: `${Math.round(19 * u)}px`, fontStyle: '700', color: css(C.ink) }).setOrigin(0, 0.5);
    // long names (translations run ~30% longer) shrink to fit rather than run under the rail
    const room = this.maxW - pad * 2 - icon - gap;
    if (label.width > room) label.setScale(room / label.width);
    const w = Math.max(h * 2, pad * 2 + icon + gap + label.displayWidth);
    const left = -w / 2 + pad;
    label.setX(left + icon + gap);
    const bg = s.add.graphics();
    bg.fillStyle(C.ink, 0.35);
    bg.fillRoundedRect(-w / 2, 3 * u, w, h, h / 2);
    bg.fillStyle(C.cream, 1);
    bg.fillRoundedRect(-w / 2, 0, w, h, h / 2);
    bg.lineStyle(3 * u, C.ink, 1);
    bg.strokeRoundedRect(-w / 2, 0, w, h, h / 2);
    const parts: Phaser.GameObjects.GameObject[] = [bg, label];
    if (locked) {
      const f = this.art.get('ui_lock');
      parts.push(s.add.image(left + icon / 2, h / 2, f.texture, f.frame).setDisplaySize(icon, icon));
    }
    return s.add.container(this.x, this.y, parts).setDepth(9500).setScrollFactor(0, 0, true);
  }
}
